import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { isAuthenticated, ensureValidToken } from '../../services/authService';

const AuthGuard: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const checkAuth = async () => {
            try {
                // Use ensureValidToken which handles token refresh automatically
                const validToken = await ensureValidToken();
                if (validToken) {
                    setAuthenticated(true);
                } else {
                    setAuthenticated(false);
                }
            } catch (error) {
                setAuthenticated(false);
            } finally {
                setLoading(false);
            }
        };

        // Initial auth check
        checkAuth();

        // Listen for auth events
        const handleAuthLogin = () => {
            setAuthenticated(true);
        };

        const handleAuthLogout = () => {
            setAuthenticated(false);
        };

        window.addEventListener('auth:login', handleAuthLogin);
        window.addEventListener('auth:logout', handleAuthLogout);

        return () => {
            window.removeEventListener('auth:login', handleAuthLogin);
            window.removeEventListener('auth:logout', handleAuthLogout);
        };
    }, []);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                    Verifying authentication...
                </Typography>
            </Box>
        );
    }

    if (!authenticated) {
        // Redirect to login with return URL
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <Outlet />;
};

export default AuthGuard;
